import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import tripDetailsConfig from '../config/tripDetails.json';

// Admin phone numbers
const ADMIN_PHONE_NUMBERS = [
  '+15407939110',
];

function LodgingBeds() {
  const { currentUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [beds, setBeds] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewAsNonAdmin, setViewAsNonAdmin] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBed, setEditingBed] = useState(null);
  const [newBed, setNewBed] = useState({
    roomName: '',
    bedName: '',
    maxSpots: 1,
    sortOrder: 0
  });

  const isActualAdmin = currentUser && ADMIN_PHONE_NUMBERS.includes(currentUser.phoneNumber);
  const isAdmin = isActualAdmin && !viewAsNonAdmin;

  const getBackRoute = () => {
    if (isActualAdmin && !viewAsNonAdmin) return '/admin';
    return location.state?.from || '/trip-details';
  };

  const defaultBeds = useMemo(() => tripDetailsConfig?.lodgingBeds?.defaultBeds || [], []);
  const seedDefaultsIfEmpty = !!tripDetailsConfig?.lodgingBeds?.seedDefaultsIfEmpty;

  const getUserName = (userId) => {
    const response = responses.find(r => r.userId === userId);
    return response?.userName || 'Unknown';
  };

  const getNextSortOrder = () => {
    const max = beds.reduce((acc, b) => Math.max(acc, Number(b.sortOrder) || 0), 0);
    return max + 1;
  };

  const seedBedsIfEmpty = async () => {
    if (!db) return;
    if (!seedDefaultsIfEmpty) return;
    if (!defaultBeds.length) return;

    const snapshot = await getDocs(collection(db, 'lodgingBeds'));
    if (!snapshot.empty) return;

    const nowIso = new Date().toISOString();
    const batch = writeBatch(db);

    defaultBeds.forEach((b, idx) => {
      const roomName = (b.roomName || '').trim();
      const bedName = (b.bedName || '').trim();
      if (!roomName || !bedName) return;

      const maxSpots = Number(b.maxSpots) || 1;
      const sortOrder = (Number.isFinite(Number(b.sortOrder)) ? Number(b.sortOrder) : (idx + 1));

      const ref = doc(collection(db, 'lodgingBeds'));
      batch.set(ref, {
        roomName,
        bedName,
        maxSpots,
        sortOrder,
        occupants: [],
        createdAt: nowIso,
        updatedAt: nowIso
      });
    });

    await batch.commit();
  };

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
      return;
    }

    let bedsUnsub = null;
    let responsesUnsub = null;

    const checkConfirmedStatusAndLoad = async () => {
      if (!isActualAdmin) {
        try {
          // Non-admins must be confirmed to access.
          const responsesRef = collection(db, 'responses');
          const q = query(responsesRef, where('userId', '==', currentUser.uid));
          const querySnapshot = await getDocs(q);
          const current = querySnapshot.empty ? null : querySnapshot.docs[0].data();
          const confirmed = current?.status === 'confirmed';
          if (!confirmed) {
            navigate('/home', {
              state: {
                message: 'You must be confirmed for the trip to access lodging & beds. Please contact an admin.'
              }
            });
            return;
          }
        } catch (e) {
          console.warn('Error checking confirmed status:', e);
          navigate('/home', {
            state: {
              message: 'You must be confirmed for the trip to access lodging & beds. Please contact an admin.'
            }
          });
          return;
        }
      }

      try {
        await seedBedsIfEmpty();

        const bedsQuery = query(collection(db, 'lodgingBeds'), orderBy('sortOrder', 'asc'));
        bedsUnsub = onSnapshot(
          bedsQuery,
          (snapshot) => {
            const data = snapshot.docs.map(d => ({
              id: d.id,
              ...d.data()
            }));
            setBeds(data);
            setLoading(false);
            setError('');
          },
          (err) => {
            console.warn('Error loading lodgingBeds:', err);
            setError('Failed to load lodging beds. Please refresh the page.');
            setLoading(false);
          }
        );

        const responsesQuery = query(collection(db, 'responses'));
        responsesUnsub = onSnapshot(
          responsesQuery,
          (snapshot) => {
            const data = snapshot.docs.map(d => ({
              id: d.id,
              ...d.data()
            }));
            setResponses(data);
          },
          (err) => {
            console.warn('Error loading responses:', err);
          }
        );
      } catch (e) {
        console.error('Error setting up lodging beds listeners:', e);
        setError('Failed to load lodging beds. Please refresh the page.');
        setLoading(false);
      }
    };

    checkConfirmedStatusAndLoad();
    return () => {
      if (bedsUnsub) bedsUnsub();
      if (responsesUnsub) responsesUnsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate, isActualAdmin]);

  const handleSelectBed = async (bedId) => {
    if (!currentUser) return;
    setError('');

    try {
      const selected = beds.find(b => b.id === bedId);
      if (!selected) return;

      const selectedOccupants = selected.occupants || [];
      const available = (selected.maxSpots || 0) - selectedOccupants.length;
      if (available <= 0) {
        setError('That bed is full.');
        return;
      }

      if (selectedOccupants.includes(currentUser.uid)) {
        setError('You are already assigned to that bed.');
        return;
      }

      // Remove user from any other bed first.
      const otherBeds = beds.filter(b => b.id !== bedId && (b.occupants || []).includes(currentUser.uid));
      for (const other of otherBeds) {
        const updated = (other.occupants || []).filter(uid => uid !== currentUser.uid);
        await updateDoc(doc(db, 'lodgingBeds', other.id), {
          occupants: updated,
          updatedAt: new Date().toISOString()
        });
      }

      // Add to selected bed.
      await updateDoc(doc(db, 'lodgingBeds', bedId), {
        occupants: [...selectedOccupants, currentUser.uid],
        updatedAt: new Date().toISOString()
      });
    } catch (e) {
      console.error('Error selecting bed:', e);
      setError('Failed to select bed. Please try again.');
    }
  };

  const handleLeaveBed = async (bedId) => {
    if (!currentUser) return;
    setError('');

    try {
      const bed = beds.find(b => b.id === bedId);
      if (!bed) return;

      const updated = (bed.occupants || []).filter(uid => uid !== currentUser.uid);
      await updateDoc(doc(db, 'lodgingBeds', bedId), {
        occupants: updated,
        updatedAt: new Date().toISOString()
      });
    } catch (e) {
      console.error('Error leaving bed:', e);
      setError('Failed to leave bed. Please try again.');
    }
  };

  const handleMoveOccupant = async (fromBedId, toBedId, userId) => {
    setError('');
    try {
      const fromBed = beds.find(b => b.id === fromBedId);
      const toBed = beds.find(b => b.id === toBedId);
      if (!fromBed || !toBed) return;

      const toOccupants = toBed.occupants || [];
      if (toOccupants.length >= (toBed.maxSpots || 0)) {
        setError('Destination bed is full.');
        return;
      }

      const fromUpdated = (fromBed.occupants || []).filter(uid => uid !== userId);
      await updateDoc(doc(db, 'lodgingBeds', fromBedId), {
        occupants: fromUpdated,
        updatedAt: new Date().toISOString()
      });

      await updateDoc(doc(db, 'lodgingBeds', toBedId), {
        occupants: [...toOccupants, userId],
        updatedAt: new Date().toISOString()
      });
    } catch (e) {
      console.error('Error moving occupant:', e);
      setError('Failed to move occupant. Please try again.');
    }
  };

  const handleRemoveOccupant = async (bedId, userId) => {
    if (!window.confirm('Remove this person from the bed?')) return;
    setError('');

    try {
      const bed = beds.find(b => b.id === bedId);
      if (!bed) return;

      const updated = (bed.occupants || []).filter(uid => uid !== userId);
      await updateDoc(doc(db, 'lodgingBeds', bedId), {
        occupants: updated,
        updatedAt: new Date().toISOString()
      });
    } catch (e) {
      console.error('Error removing occupant:', e);
      setError('Failed to remove occupant. Please try again.');
    }
  };

  const handleAssignUserToBed = async (bedId, userId) => {
    if (!userId) return;
    setError('');
    try {
      const selected = beds.find(b => b.id === bedId);
      if (!selected) return;

      const selectedOccupants = selected.occupants || [];
      const available = (selected.maxSpots || 0) - selectedOccupants.length;
      if (available <= 0) {
        setError('That bed is full.');
        return;
      }

      // Remove from other beds first.
      const otherBeds = beds.filter(b => b.id !== bedId && (b.occupants || []).includes(userId));
      for (const other of otherBeds) {
        const updated = (other.occupants || []).filter(uid => uid !== userId);
        await updateDoc(doc(db, 'lodgingBeds', other.id), {
          occupants: updated,
          updatedAt: new Date().toISOString()
        });
      }

      // Add to selected bed if not already there.
      if (!selectedOccupants.includes(userId)) {
        await updateDoc(doc(db, 'lodgingBeds', bedId), {
          occupants: [...selectedOccupants, userId],
          updatedAt: new Date().toISOString()
        });
      }
    } catch (e) {
      console.error('Error assigning user to bed:', e);
      setError('Failed to assign bed. Please try again.');
    }
  };

  const handleCreateBed = async () => {
    if (!newBed.roomName.trim() || !newBed.bedName.trim()) {
      setError('Please enter a room name and bed name.');
      return;
    }

    const maxSpots = parseInt(newBed.maxSpots, 10);
    if (!Number.isFinite(maxSpots) || maxSpots <= 0) {
      setError('Max spots must be a positive number.');
      return;
    }

    const sortOrderValue = Number.isFinite(Number(newBed.sortOrder)) && Number(newBed.sortOrder) !== 0
      ? Number(newBed.sortOrder)
      : getNextSortOrder();

    try {
      const nowIso = new Date().toISOString();
      await addDoc(collection(db, 'lodgingBeds'), {
        roomName: newBed.roomName.trim(),
        bedName: newBed.bedName.trim(),
        maxSpots,
        sortOrder: sortOrderValue,
        occupants: [],
        createdAt: nowIso,
        updatedAt: nowIso
      });

      setShowCreateModal(false);
      setNewBed({ roomName: '', bedName: '', maxSpots: 1, sortOrder: 0 });
      setError('');
    } catch (e) {
      console.error('Error creating bed:', e);
      setError('Failed to create bed. Please try again.');
    }
  };

  const handleUpdateBed = async () => {
    if (!editingBed) return;
    if (!editingBed.roomName?.trim() || !editingBed.bedName?.trim()) {
      setError('Please enter a room name and bed name.');
      return;
    }
    const maxSpots = parseInt(editingBed.maxSpots, 10);
    if (!Number.isFinite(maxSpots) || maxSpots <= 0) {
      setError('Max spots must be a positive number.');
      return;
    }
    const sortOrderValue = Number.isFinite(Number(editingBed.sortOrder)) ? Number(editingBed.sortOrder) : 0;

    const occupants = editingBed.occupants || [];
    if (occupants.length > maxSpots) {
      setError('Max spots cannot be less than the current number of assigned people.');
      return;
    }

    try {
      await updateDoc(doc(db, 'lodgingBeds', editingBed.id), {
        roomName: editingBed.roomName.trim(),
        bedName: editingBed.bedName.trim(),
        maxSpots,
        sortOrder: sortOrderValue,
        updatedAt: new Date().toISOString()
      });
      setEditingBed(null);
      setError('');
    } catch (e) {
      console.error('Error updating bed:', e);
      setError('Failed to update bed. Please try again.');
    }
  };

  const handleDeleteBed = async (bedId) => {
    const bed = beds.find(b => b.id === bedId);
    const assignedCount = (bed?.occupants || []).length;
    const msg = assignedCount > 0
      ? `Delete this bed? This will unassign ${assignedCount} people.`
      : 'Delete this bed?';
    if (!window.confirm(msg)) return;

    try {
      await deleteDoc(doc(db, 'lodgingBeds', bedId));
      setError('');
    } catch (e) {
      console.error('Error deleting bed:', e);
      setError('Failed to delete bed. Please try again.');
    }
  };

  const unassignedConfirmedUsers = useMemo(() => {
    const confirmed = responses.filter(r => r.status === 'confirmed' || !r.status);
    const assignedUserIds = new Set();
    beds.forEach(b => (b.occupants || []).forEach(uid => assignedUserIds.add(uid)));
    return confirmed.filter(r => r.userId && !assignedUserIds.has(r.userId));
  }, [beds, responses]);

  const currentUserBed = useMemo(() => {
    if (!currentUser?.uid) return null;
    return beds.find(b => (b.occupants || []).includes(currentUser.uid)) || null;
  }, [beds, currentUser?.uid]);

  const roomsForRender = useMemo(() => {
    const sortedBeds = [...beds].sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0));
    const roomOrder = [];
    const byRoom = new Map();

    sortedBeds.forEach((b) => {
      const roomName = (b.roomName || 'Room').trim() || 'Room';
      if (!byRoom.has(roomName)) {
        byRoom.set(roomName, []);
        roomOrder.push(roomName);
      }
      byRoom.get(roomName).push(b);
    });

    return roomOrder.map(roomName => ({ roomName, beds: byRoom.get(roomName) || [] }));
  }, [beds]);

  const existingRoomNames = useMemo(() => {
    const set = new Set();
    beds.forEach(b => {
      const rn = (b.roomName || '').trim();
      if (rn) set.add(rn);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [beds]);

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          <div className="loading">Loading lodging & beds...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <h1 className="card-title">🛏️ {tripDetailsConfig?.lodgingBeds?.title || 'Lodging & Beds'}</h1>
              <p className="card-subtitle">{tripDetailsConfig?.lodgingBeds?.subtitle || 'Pick a bed.'}</p>
              {!isAdmin && currentUserBed && (
                <p style={{ margin: '10px 0 0 0', color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                  Your current bed: <strong>{currentUserBed.roomName} – {currentUserBed.bedName}</strong>
                </p>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              {isActualAdmin && (
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  background: viewAsNonAdmin ? '#e2e8f0' : 'transparent',
                  border: '1px solid var(--border-color)',
                  fontSize: '0.85rem',
                  color: 'var(--text-secondary)'
                }}>
                  <input
                    type="checkbox"
                    checked={viewAsNonAdmin}
                    onChange={(e) => setViewAsNonAdmin(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  View as User
                </label>
              )}
              {isAdmin && (
                <button
                  onClick={() => {
                    setNewBed({
                      roomName: existingRoomNames[0] || '',
                      bedName: '',
                      maxSpots: 1,
                      sortOrder: getNextSortOrder()
                    });
                    setShowCreateModal(true);
                  }}
                  className="btn btn-primary"
                  style={{
                    width: 'auto',
                    padding: '8px 16px',
                    fontSize: '0.9rem',
                    borderRadius: '8px'
                  }}
                >
                  + Create Bed
                </button>
              )}
              <button
                onClick={() => navigate(getBackRoute())}
                className="btn btn-secondary"
                style={{
                  width: 'auto',
                  padding: '8px 16px',
                  fontSize: '0.9rem',
                  borderRadius: '8px'
                }}
              >
                ← Back
              </button>
            </div>
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {isAdmin && (
          <div style={{
            background: 'var(--bg-secondary)',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid var(--border-color)',
            marginTop: '15px'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: 'var(--text-primary)', fontSize: '1rem' }}>
              Unassigned (confirmed) people: {unassignedConfirmedUsers.length}
            </h3>
            {unassignedConfirmedUsers.length === 0 ? (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                Everyone is assigned to a bed.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {unassignedConfirmedUsers.map(u => (
                  <div
                    key={u.userId}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      background: 'var(--bg-primary)',
                      borderRadius: '10px',
                      border: '1px solid var(--border-color)'
                    }}
                  >
                    <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                      {u.userName}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <select
                        defaultValue=""
                        onChange={(e) => {
                          if (e.target.value) {
                            handleAssignUserToBed(e.target.value, u.userId);
                            e.target.value = '';
                          }
                        }}
                        style={{
                          padding: '6px 10px',
                          borderRadius: '8px',
                          border: '2px solid var(--border-color)',
                          fontSize: '0.85rem',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      >
                        <option value="">Assign to a bed...</option>
                        {beds
                          .filter(b => (b.occupants || []).length < (b.maxSpots || 0))
                          .map(b => (
                            <option key={b.id} value={b.id}>
                              {b.roomName} – {b.bedName} ({(b.maxSpots || 0) - (b.occupants || []).length} spots left)
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', marginTop: '20px' }}>
          {roomsForRender.map((room) => (
            <div
              key={room.roomName}
              style={{
                background: 'var(--bg-primary)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid var(--border-color)',
                boxShadow: '0 2px 4px var(--shadow)'
              }}
            >
              <h2 style={{ margin: '0 0 12px 0', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
                {room.roomName}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {room.beds.map((bed) => {
                  const occupants = bed.occupants || [];
                  const available = (bed.maxSpots || 0) - occupants.length;
                  const isCurrentUsersBed = !!currentUser?.uid && occupants.includes(currentUser.uid);

                  return (
                    <div
                      key={bed.id}
                      style={{
                        padding: '14px',
                        background: isCurrentUsersBed 
                          ? (isDark ? '#0f766e' : '#e6fffa') 
                          : 'var(--bg-secondary)',
                        borderRadius: '12px',
                        border: isCurrentUsersBed 
                          ? (isDark ? '1px solid #14b8a6' : '1px solid #81e6d9') 
                          : '1px solid var(--border-color)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '180px' }}>
                          <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                            {bed.bedName}
                          </div>
                          <div style={{ fontSize: '0.9rem', color: 'var(--text-tertiary)', marginTop: '4px' }}>
                            {occupants.length} / {bed.maxSpots} spots filled{' '}
                            {available > 0 ? (
                              <span style={{ color: '#2f855a', fontWeight: 700 }}>({available} available)</span>
                            ) : (
                              <span style={{ color: '#c53030', fontWeight: 700 }}>(Full)</span>
                            )}
                          </div>
                        </div>

                        {isAdmin && (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <button
                              onClick={() => setEditingBed({ ...bed })}
                              className="btn btn-secondary"
                              style={{
                                width: 'auto',
                                padding: '6px 12px',
                                fontSize: '0.85rem',
                                borderRadius: '8px'
                              }}
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => handleDeleteBed(bed.id)}
                              className="btn btn-secondary"
                              style={{
                                width: 'auto',
                                padding: '6px 12px',
                                fontSize: '0.85rem',
                                borderRadius: '8px',
                                background: '#fed7d7',
                                color: '#c53030',
                                border: '1px solid #feb2b2'
                              }}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        )}

                        {!isAdmin && (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {!isCurrentUsersBed && available > 0 && (
                              <button
                                onClick={() => handleSelectBed(bed.id)}
                                className="btn btn-primary"
                                style={{
                                  width: 'auto',
                                  padding: '8px 14px',
                                  fontSize: '0.9rem',
                                  borderRadius: '10px'
                                }}
                              >
                                Select
                              </button>
                            )}
                            {isCurrentUsersBed && (
                              <button
                                onClick={() => handleLeaveBed(bed.id)}
                                className="btn btn-secondary"
                                style={{
                                  width: 'auto',
                                  padding: '8px 14px',
                                  fontSize: '0.9rem',
                                  borderRadius: '10px',
                                  background: '#fed7d7',
                                  color: '#c53030',
                                  border: '1px solid #feb2b2'
                                }}
                              >
                                Leave
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      <div style={{ marginTop: '12px' }}>
                        <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-secondary)', marginBottom: '8px' }}>
                          People:
                        </div>
                        {occupants.length === 0 ? (
                          <div style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', fontSize: '0.9rem' }}>
                            No one assigned yet
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {occupants.map((userId) => {
                              const isCurrentUser = userId === currentUser?.uid;
                              const userName = getUserName(userId);
                              return (
                                <div
                                  key={userId}
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '10px 12px',
                                    background: isCurrentUser 
                                      ? (isDark ? '#0f766e' : '#e6fffa') 
                                      : 'var(--bg-primary)',
                                    borderRadius: '10px',
                                    border: isCurrentUser 
                                      ? (isDark ? '1px solid #14b8a6' : '1px solid #81e6d9') 
                                      : '1px solid var(--border-color)'
                                  }}
                                >
                                  <div style={{ color: 'var(--text-primary)', fontWeight: isCurrentUser ? 700 : 600 }}>
                                    {userName} {isCurrentUser && '(You)'}
                                  </div>

                                  {isAdmin && (
                                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                      {beds
                                        .filter(b => b.id !== bed.id && (b.occupants || []).length < (b.maxSpots || 0))
                                        .map(target => (
                                          <button
                                            key={target.id}
                                            onClick={() => handleMoveOccupant(bed.id, target.id, userId)}
                                            style={{
                                              padding: '4px 8px',
                                              fontSize: '0.75rem',
                                              borderRadius: '6px',
                                              background: '#e2e8f0',
                                              color: '#2d3748',
                                              border: '1px solid #cbd5e0',
                                              cursor: 'pointer',
                                              fontWeight: 600
                                            }}
                                            title={`Move to ${target.roomName} – ${target.bedName}`}
                                          >
                                            → {target.bedName.substring(0, 14)}
                                          </button>
                                        ))}
                                      <button
                                        onClick={() => handleRemoveOccupant(bed.id, userId)}
                                        style={{
                                          padding: '4px 8px',
                                          fontSize: '0.75rem',
                                          borderRadius: '6px',
                                          background: '#fed7d7',
                                          color: '#c53030',
                                          border: '1px solid #feb2b2',
                                          cursor: 'pointer',
                                          fontWeight: 700
                                        }}
                                      >
                                        Remove
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {isAdmin && (
                        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <select
                            defaultValue=""
                            onChange={(e) => {
                              if (e.target.value) {
                                handleAssignUserToBed(bed.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                            style={{
                              padding: '8px 10px',
                              borderRadius: '10px',
                              border: '2px solid var(--border-color)',
                              fontSize: '0.9rem',
                              background: 'var(--bg-primary)',
                              color: 'var(--text-primary)'
                            }}
                          >
                            <option value="">Assign unassigned person...</option>
                            {unassignedConfirmedUsers.map(u => (
                              <option key={u.userId} value={u.userId}>
                                {u.userName}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'var(--bg-primary)',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '520px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Create Bed</h2>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Room Name *
                </label>
                <input
                  type="text"
                  list="room-names"
                  value={newBed.roomName}
                  onChange={(e) => setNewBed({ ...newBed, roomName: e.target.value })}
                  placeholder="e.g., Bedroom 2"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
                <datalist id="room-names">
                  {existingRoomNames.map((rn) => (
                    <option key={rn} value={rn} />
                  ))}
                </datalist>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Bed Name *
                </label>
                <input
                  type="text"
                  value={newBed.bedName}
                  onChange={(e) => setNewBed({ ...newBed, bedName: e.target.value })}
                  placeholder="e.g., Queen Bed A"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Max Spots *
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={newBed.maxSpots}
                  onChange={(e) => setNewBed({ ...newBed, maxSpots: parseInt(e.target.value, 10) || 1 })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Sort Order
                </label>
                <input
                  type="number"
                  value={newBed.sortOrder}
                  onChange={(e) => setNewBed({ ...newBed, sortOrder: parseInt(e.target.value, 10) || 0 })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
                <p style={{ margin: '6px 0 0 0', fontSize: '0.85rem', color: 'var(--text-tertiary)' }}>
                  Lower numbers appear first.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateBed}
                  className="btn btn-primary"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        {editingBed && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'var(--bg-primary)',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '520px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <h2 style={{ marginTop: 0, marginBottom: '8px' }}>Edit Bed</h2>
              <p style={{ marginTop: 0, marginBottom: '20px', color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                {editingBed.roomName} – {editingBed.bedName}
              </p>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Room Name *
                </label>
                <input
                  type="text"
                  list="room-names-edit"
                  value={editingBed.roomName || ''}
                  onChange={(e) => setEditingBed({ ...editingBed, roomName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
                <datalist id="room-names-edit">
                  {existingRoomNames.map((rn) => (
                    <option key={rn} value={rn} />
                  ))}
                </datalist>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Bed Name *
                </label>
                <input
                  type="text"
                  value={editingBed.bedName || ''}
                  onChange={(e) => setEditingBed({ ...editingBed, bedName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Max Spots *
                </label>
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={editingBed.maxSpots}
                  onChange={(e) => setEditingBed({ ...editingBed, maxSpots: parseInt(e.target.value, 10) || 1 })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
                {(editingBed.occupants || []).length > (parseInt(editingBed.maxSpots, 10) || 0) && (
                  <p style={{ color: '#c53030', fontSize: '0.85rem', marginTop: '6px' }}>
                    Warning: max spots is less than currently assigned people.
                  </p>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600 }}>
                  Sort Order
                </label>
                <input
                  type="number"
                  value={editingBed.sortOrder || 0}
                  onChange={(e) => setEditingBed({ ...editingBed, sortOrder: parseInt(e.target.value, 10) || 0 })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '2px solid var(--border-color)',
                    fontSize: '1rem'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setEditingBed(null);
                    setError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateBed}
                  className="btn btn-primary"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LodgingBeds;

